// SceneBoard — storyboard panel for the script editor (video pipeline R1).
//
// Renders the ContiPackage produced by the decompose phase: a meta row
// (scene count, total runtime, BGM mood) plus one card per Scene showing the
// narration (TTS input), the English visual prompt (ComfyUI input), the
// subtitle, and style/motion badges. A "스토리보드 생성/재생성" button drives the
// decompose mutation. This is the bridge view between the text script and the
// GPU render pipeline (R2+).

import { useState } from 'react'
import { Check, Clapperboard, Copy, Download, Film, Play, RefreshCcw } from 'lucide-react'
import { Badge, Button, Card } from '@/admin-design/_common'
import type { ContiPackage, RenderStatus, Scene, Thumbnail, ThumbnailCandidate } from '@/api/types'

interface SceneBoardProps {
  conti?: ContiPackage
  /** True while a decompose mutation is in flight. */
  loading?: boolean
  /** Disabled until the project has a draft to decompose from. */
  canDecompose?: boolean
  onDecompose?: () => void
  /** Decompose error message, if the last attempt failed. */
  error?: string
  renderStatus?: RenderStatus
  renderLoading?: boolean
  canRender?: boolean
  onRender?: () => void
  downloadHref?: string
  renderError?: string
}

export default function SceneBoard({
  conti,
  loading = false,
  canDecompose = true,
  onDecompose,
  error,
  renderStatus,
  renderLoading = false,
  canRender = false,
  onRender,
  downloadHref,
  renderError,
}: SceneBoardProps) {
  const hasExisting = Boolean(conti && conti.scenes.length > 0)
  const isRendering = renderStatus?.state === 'running' || renderLoading
  const canDownload = renderStatus?.state === 'done' && Boolean(downloadHref)
  const progressTotal = renderStatus?.total ?? conti?.scenes.length ?? 0
  const progressDone = renderStatus?.done ?? 0
  const progressPct = progressTotal > 0 ? Math.min(100, Math.round((progressDone / progressTotal) * 100)) : 0

  return (
    <Card>
      <header className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-card-foreground">
          <Clapperboard size={14} /> Storyboard
        </span>
        {hasExisting && (
          <span className="text-[11px] text-muted-foreground">
            {conti!.scenes.length} scenes · ~{conti!.total_duration_sec}s
            {conti!.bgm_mood ? ` · BGM ${conti!.bgm_mood}` : ''}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {hasExisting && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onRender}
              disabled={!canRender || isRendering}
              loading={isRendering}
              className="h-7 px-2 text-xs"
              title={canRender ? undefined : 'ComfyUI/TTS 설정과 Storyboard가 필요합니다'}
            >
              {!isRendering && <Play size={12} />}
              {isRendering ? '생성 중…' : '영상 생성'}
            </Button>
          )}
          {canDownload && (
            <a
              href={downloadHref}
              className="inline-flex h-7 items-center gap-1 rounded-md bg-secondary px-2 text-xs font-medium text-secondary-foreground shadow-sm hover:bg-secondary/80"
            >
              <Download size={12} /> 다운로드
            </a>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={onDecompose}
            disabled={!canDecompose}
            loading={loading}
            className="h-7 px-2 text-xs"
            title={canDecompose ? undefined : 'Draft를 먼저 생성하세요'}
          >
            {!loading && <RefreshCcw size={12} />}
            {loading ? '분해 중…' : hasExisting ? '재생성' : '스토리보드 생성'}
          </Button>
        </div>
      </header>

      <div className="p-3">
        {error && (
          <p className="mb-3 rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
            {error}
          </p>
        )}
        {renderError && (
          <p className="mb-3 rounded border border-destructive-soft bg-destructive-soft p-2 text-[11px] text-destructive-soft-foreground">
            {renderError}
          </p>
        )}
        {renderStatus && (
          <div className="mb-3 rounded border border-border bg-muted/20 p-2">
            <div className="mb-1 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{renderStatus.state}</span>
              <span>
                {progressDone}/{progressTotal}
              </span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div className="h-full bg-primary transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {!hasExisting ? (
          <p className="text-xs italic text-muted-foreground">
            {canDecompose
              ? 'Draft를 장면별 스토리보드(내레이션·비주얼 프롬프트·자막)로 분해합니다. “스토리보드 생성”을 눌러주세요.'
              : 'Draft가 아직 없습니다. 먼저 스크립트를 생성한 뒤 분해할 수 있어요.'}
          </p>
        ) : (
          <div className="space-y-2">
            {conti!.scenes.map((scene) => (
              <SceneCard key={scene.idx} scene={scene} />
            ))}
            {conti!.tags && conti!.tags.length > 0 && <TagsCard tags={conti!.tags} />}
            {thumbCandidates(conti!.thumbnail).map((c, i, arr) => (
              <ThumbnailCard
                key={i}
                idx={arr.length > 1 ? i + 1 : undefined}
                concept={c.concept}
                prompt={c.prompt}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

const styleVariant: Record<Scene['visual_style'], 'info' | 'success' | 'warning'> = {
  real: 'info',
  anime: 'success',
  logo: 'warning',
}

function SceneCard({ scene }: { scene: Scene }) {
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-1.5">
        <Badge variant="muted" className="font-mono text-[10px] rounded">
          #{scene.idx + 1}
        </Badge>
        <Badge variant={styleVariant[scene.visual_style]} className="text-[10px] rounded">
          {scene.visual_style}
        </Badge>
        <Badge variant="muted" className="text-[10px] rounded">
          {scene.motion}
        </Badge>
        <span className="text-[11px] text-muted-foreground">~{scene.duration_sec.toFixed(1)}s</span>
      </div>

      <Field label="Narration" value={scene.narration} mono={false} />
      <Field label="Visual prompt" value={scene.visual_prompt} mono />
      {scene.subtitle && scene.subtitle !== scene.narration && (
        <Field label="Subtitle" value={scene.subtitle} mono={false} />
      )}
    </div>
  )
}

// thumbCandidates normalizes a Thumbnail into a candidate list: prefer the
// `candidates` set (migration 0028), else fall back to the single hero.
function thumbCandidates(t?: Thumbnail): ThumbnailCandidate[] {
  if (!t) return []
  if (t.candidates && t.candidates.length > 0) return t.candidates
  if (t.prompt || t.concept) return [{ concept: t.concept, prompt: t.prompt }]
  return []
}

function ThumbnailCard({ idx, concept, prompt }: { idx?: number; concept: string; prompt: string }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-card-foreground">
        <Film size={12} /> Thumbnail{idx ? ` 후보 ${idx}` : ''}
      </div>
      {concept && <Field label="Concept" value={concept} mono={false} />}
      {prompt && <Field label="Prompt" value={prompt} mono />}
    </div>
  )
}

// TagsCard shows the merged tag list in both YouTube-ready formats: a
// hash-prefixed space-joined line and a comma-joined line, each copyable.
function tagHashLine(tags: string[]): string {
  return tags.map((t) => '#' + t.replace(/^#/, '').trim()).filter((t) => t !== '#').join(' ')
}
function tagCsvLine(tags: string[]): string {
  return tags.map((t) => t.replace(/^#/, '').trim()).filter(Boolean).join(', ')
}

function TagsCard({ tags }: { tags: string[] }) {
  return (
    <div className="rounded-md border border-dashed border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-card-foreground">
        <Film size={12} /> Tags <span className="font-normal text-muted-foreground">({tags.length})</span>
      </div>
      <Field label="# 해시태그" value={tagHashLine(tags)} mono={false} />
      <Field label=", 쉼표" value={tagCsvLine(tags)} mono={false} />
    </div>
  )
}

function Field({ label, value, mono }: { label: string; value: string; mono: boolean }) {
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable — non-fatal
    }
  }
  return (
    <div className="group mb-1.5 last:mb-0">
      <div className="flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        <button
          type="button"
          onClick={copy}
          className="opacity-0 transition group-hover:opacity-100 text-muted-foreground hover:text-foreground"
          aria-label={`copy ${label}`}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
        </button>
      </div>
      <p
        className={
          mono
            ? 'whitespace-pre-wrap font-mono text-[12px] leading-relaxed text-card-foreground'
            : 'whitespace-pre-wrap text-[13px] leading-relaxed text-card-foreground'
        }
      >
        {value}
      </p>
    </div>
  )
}
